<template>
  <div class="panel panel-default relative" :class="{'has-error': hasErrors}" style="margin-top: 5px">
    <b-card no-body :border-variant="borderColor">
      <b-card-header header-tag="header" class="p-1" role="tab">
        <b-button class="btn-doc" v-b-toggle="documentKey" @click.once="requestFiles" squared variant="link"
                  style="width: 100%; padding: 2px; text-align: left; overflow: hidden;"
        >
          {{ documentName }}
        </b-button>
      </b-card-header>
      <b-collapse :id="documentKey" class="panel-collapse relative">
        <div class="panel-body relative">
          <dashboard
            v-if="dashboardShowCondition"
            :id="`dashboard-${documentKey}`"
            :key="`dashboard${$vnode.key}`"
            :props="dashboardOptions"
            :uppy="uppy">
          </dashboard>
          <div v-if="documentsShowCondition">
            <CoolLightBox
                :items="documents || []"
                :index="applicationDocumentPreviewIndex"
                :useZoomBar="true"
                @close="applicationDocumentPreviewIndex = null"
            />
            <div class="images-wrapper" style="display: inline-flex; flex-direction: row; flex-wrap: wrap; width: 100%">
              <div style="width: 100%; text-align: center; color: #d54444" v-if="documents.length === 0 && !loadingDocuments">Файлы отсутствуют</div>
              <div v-for="(doc, key) in documents || []" class="image"
                   :key="doc.key"
                   :style="{ backgroundImage: 'url(' + doc.thumb + ')' }"
                   style="width: 100px; height: 100px; display: inline; margin: 5px; background-repeat: no-repeat; background-size: 100px; background-position: center"
                   @click="applicationDocumentPreviewIndex = key"
              >
              </div>
            </div>
          </div>
        </div>
        <div v-if="loadingDocuments" class="loader"></div>
      </b-collapse>
    </b-card>
    <div v-if="documentComment" class="form-fieldhelp" style="right: 5px; top: 6px">
      <label>?</label>
      <div class="form-fieldhelp-text">
        <div>{{ documentComment }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import '@uppy/core/dist/style.css'
import '@uppy/dashboard/dist/style.css'
import applicationFormContextMixin from '../../mixins/applicationFormContextMixin';
import CoolLightBox from 'vue-cool-lightbox';
import applicationFormMixin from '../../mixins/applicationFormMixin';
import UppyUrlDrop from '../../lib/UppyUrlDrop';
import RusUppy from '@uppy/locales/lib/ru_RU';
import Uppy, { UppyOptions } from '@uppy/core';
import XHRUpload from '@uppy/xhr-upload';
import { v4 as uuidv4 } from 'uuid';
import dashboard from '@uppy/vue/src/dashboard'

export default {
  name: 'UppyUploader',

  components: {
    CoolLightBox,
    dashboard
  },

  mixins: [
    applicationFormContextMixin,
    applicationFormMixin
  ],

  computed: {
    borderColor () {
      return this.hasErrors ? 'danger' : null
    }
  },

  model: {
    prop: 'documents',
    event: 'change'
  },

  props: {
    dashboardOptions: {
      type: Object,
      default: () => ({
        browserBackButtonClose: false,
        doneButtonHandler: null,
        height: 318,
        inline: true,
        note: 'Изображения и pdf до 2Мб',
        proudlyDisplayPoweredByUppy: false,
        showLinkToFileUploadResult: true,
        showProgressDetails: true,
        showRemoveButtonAfterComplete: true,
        width: '100%'
      })
    },

    dashboardShowCondition: {
      type: Boolean,
      required: true
    },

    documentComment: {
      type: String
    },

    documentKey: {
      type: String,
      required: true
    },

    documentName: {
      type: String
    },

    documentShortName: {
      type: String
    },

    documents: {
      type: [Array, Object],
      required: true,
      default: () => []
    },

    documentsShowCondition: {
      type: Boolean,
      required: true
    },

    filesRequest: {
      type: Function
    },

    getUuid: {
      type: Function,
      required: true
    },

    hasErrors: {
      type: Boolean
    }
  },

  data () {
    const baseUrl = process.env.VUE_APP_API_BASE_URL
    RusUppy.strings.done = 'Назад'
    const uppy = new Uppy({
      id: uuidv4(),
      locale: RusUppy,
      allowMultipleUploads: true,
      autoProceed: true,
      debug: false,
      restrictions: {
        maxFileSize: 1024 * 1024 * 2,
        minFileSize: null,
        maxTotalFileSize: null,
        maxNumberOfFiles: null,
        minNumberOfFiles: 1,
        allowedFileTypes: ['application/pdf', '.jpg', '.jpeg', '.png', '.pdf', '.bmp', '.webp']
      }
    }).use(XHRUpload, {
      limit: 10,
      endpoint: baseUrl + '/upload/docs',
      formData: true,
      fieldName: 'file',
      metaFields: ['uuid', 'duplicated']
    }).use(UppyUrlDrop)

    uppy.on('file-added', (file) => {
      const uuid = file.meta.uuid ?? uuidv4()
      uppy.setFileMeta(file.id, {
        uuid
      })
      this.$emit('file-added', file)
    })

    uppy.on('file-removed', (file) => {
      this.deleteDocument(file.meta.uuid)
      this.$emit('file-removed', file)
    })

    uppy.on('upload-success', (file) => {
      const uuid = (file.meta.uuid ?? uuidv4()) + '.' + file.extension.toLowerCase()

      uppy.setFileMeta(file.id, { uuid })
      file.meta.uuid = uuid
      this.addDocument(file)

      this.$emit('upload-success', file)
    })

    return {
      applicationDocumentPreviewIndex: null,
      loadingDocuments: false,
      uppy
    }
  },

  async mounted () {
    if (this.isDuplicate || this.isDraft) {
      await this.manuallyAddFiles()
    }
  },

  async updated () {
    if (!this.isCheckOrView) {

      if (this.uppy.getFiles().length === this.documents.length) return

      await this.manuallyAddFiles()
    }
  },

  methods: {
    async manuallyAddFiles () {
      for (const document of this.documents) {
        if (!document.data && document.src) {
          const blob = await (await fetch(document.src)).blob()
          document.data = blob
          document.type = blob.type
          document.name = document.meta.uuid
        }

        const fileExists = this.uppy.getFiles().some(file => file?.meta?.uuid === document?.meta?.uuid);

        if (!fileExists) {
          const fileId = this.uppy.addFile(document)

          this.uppy.setFileState(fileId, {
            progress: {
              uploadStarted: true,
              uploadComplete: true,
              percentage: 100
            }
          })
        }
      }
    },

    addDocument (file) {
      this.$emit('change', [...this.documents, file])
    },

    deleteDocument (uuid) {
      this.$emit('change', this.documents.filter((file) => file.meta.uuid !== uuid))
    },

    getFiles () {
      return this.uppy.getFiles()
    },

    removeFile (id) {
      return this.uppy.removeFile(id)
    },

    async requestFiles () {
      if (this.isNew || this.isDraft || this.isDuplicate) {
        return
      }

      if (this.documents.length > 0) {
        return
      }

      this.loadingDocuments = true

      if (this.filesRequest) {
        await this.filesRequest(this.documentShortName)
      }

      this.loadingDocuments = false
    }
  }
}
</script>
